/**
 * The session context: is there a stored session, who is it, and sign-out.
 *
 * `hasSession` is `null` while the token store is being read, then a boolean.
 * It says a pair is STORED, not that it works — `me` is what proves it. T47's
 * `SessionGate` renders from these two.
 */

import type { AuthorizedFetcher } from "@repo/client/data/core";
import { meOptions } from "@repo/client/data/me";
import type { GetMeOutput } from "@repo/contract/iam";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDataQuery } from "./query-client";
import {
  createSessionController,
  type SessionPorts,
  type SignOutOptions,
  type SignOutResult,
} from "./session-controller";

export interface SessionValue {
  readonly hasSession: boolean | null;
  /** `GET /auth/me`, enabled only while a session is stored. */
  readonly me: ReturnType<typeof useDataQuery<GetMeOutput>>;
  /** Re-read the token store — after T47's sign-in wrote a pair. */
  refresh(): Promise<void>;
  signOut(options?: SignOutOptions): Promise<SignOutResult>;
}

const SessionContext = createContext<SessionValue | null>(null);

export interface SessionProviderProps {
  /** `account.ts`'s singleton; `enter` is this file's legacy claim below. */
  readonly accountScope: SessionPorts["accountScope"] & {
    enter(accountId: string): Promise<unknown>;
  };
  readonly api: SessionPorts["api"] & AuthorizedFetcher;
  readonly children: ReactNode;
  readonly clearCaches: SessionPorts["clearCaches"];
  /** Called once a stored session exists (drain what waited for a sign-in). */
  readonly onSignedIn?: () => void;
  readonly outbox: SessionPorts["outbox"];
  /** Subscribe to `ApiClient`'s `onSessionEnded`; returns the unsubscribe. */
  readonly subscribeSessionEnded: (listener: () => void) => () => void;
}

export function SessionProvider({
  accountScope,
  api,
  children,
  clearCaches,
  onSignedIn,
  outbox,
  subscribeSessionEnded,
}: SessionProviderProps) {
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    setHasSession(await api.hasSession());
  }, [api]);

  useEffect(() => {
    refresh().catch(() => setHasSession(false));
  }, [refresh]);

  // A session the SERVER ended (a refresh that came back 401, an account
  // deletion) arrives here through `onSessionEnded`, as does a sign-out.
  // Nothing the next person sees may come from this one's cache.
  useEffect(
    () =>
      subscribeSessionEnded(() => {
        setHasSession(false);
        Promise.resolve(clearCaches()).catch((error: unknown) => {
          console.warn("[data] cache clear failed", error);
        });
      }),
    [subscribeSessionEnded, clearCaches]
  );

  useEffect(() => {
    if (hasSession === true) {
      onSignedIn?.();
    }
  }, [hasSession, onSignedIn]);

  const me = useDataQuery(meOptions(api), { enabled: hasSession === true });

  // The legacy claim. An install from before the account was recorded is
  // signed in with nothing stored, so its queued writes belong to no one and
  // a sign-out would drop none of them. `me` is the first proof of who this
  // is; with no previous account, claiming it runs no cleanup listener.
  const meAccountId = me.data?.accountId;
  useEffect(() => {
    if (meAccountId !== undefined && accountScope.current() === null) {
      accountScope.enter(meAccountId).catch((error: unknown) => {
        console.warn("[data] account claim failed", error);
      });
    }
  }, [accountScope, meAccountId]);

  const controller = useMemo(
    () => createSessionController({ accountScope, api, clearCaches, outbox }),
    [accountScope, api, clearCaches, outbox]
  );

  const value = useMemo<SessionValue>(
    () => ({ hasSession, me, refresh, signOut: controller.signOut }),
    [hasSession, me, refresh, controller]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error("useSession must be used inside DataProvider");
  }
  return session;
}
